import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Icon,
  Input,
  InputGroup,
  Spinner,
  Stack,
  Text,
} from '@chakra-ui/react';
import {
  ArrowTrendingUpIcon,
  BookOpenIcon,
  DocumentIcon,
  LinkIcon,
  MagnifyingGlassIcon,
  PrinterIcon,
  SparklesIcon,
  Square3Stack3DIcon,
  ViewfinderCircleIcon,
} from '@heroicons/react/24/outline';
import { useParams } from 'react-router-dom';
import {
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from '@/components/ui/dialog';
import { toaster } from '@/components/ui/toaster';
import api from '@/utils/api';
import { buildPublicUrl } from '@/utils/public-url';
import { Content } from '@/lib/types';
import { slotFor } from '@/lib/slot-color';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onPrint: (item: Content) => void | Promise<void>;
};

// Semantic icon per content type. Colour comes from the DS tag-slot palette
// (slotFor) at the call site, so it groups by type name, not meaning.
function pickIcon(name: string | undefined): typeof ViewfinderCircleIcon {
  const n = (name || '').toLowerCase();
  if (n.includes('drill')) return ViewfinderCircleIcon;
  if (n.includes('skill')) return SparklesIcon;
  if (n.includes('play')) return ArrowTrendingUpIcon;
  if (n.includes('tactic')) return Square3Stack3DIcon;
  if (n.includes('document')) return DocumentIcon;
  if (n.includes('article') || n.includes('post')) return BookOpenIcon;
  return DocumentIcon;
}

async function ensurePublicLink(item: Content): Promise<string | null> {
  let fallback: string | null = item.public_link || null;
  if (!item.public_link) {
    try {
      const res = await api.put(`contents/public/${item.id}`);
      fallback = res.data?.data?.public_link || null;
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to generate public link',
      });
      return null;
    }
  }
  if (item.reference_id) return buildPublicUrl('content', item.reference_id);
  return fallback;
}

export default function ShareContentDialog({ isOpen, onClose, onPrint }: Props) {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [results, setResults] = useState<Content[]>([]);
  const [recent, setRecent] = useState<Content[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setQuery('');
    setDebouncedQuery('');
    setResults([]);
    if (recent.length === 0 && workspaceId) {
      api
        .get(`contents/recent/${workspaceId}?limit=8`)
        .then((res) => setRecent(res.data.data || []))
        .catch(() => undefined);
    }
    const t = setTimeout(() => inputRef.current?.focus(), 50);
    return () => clearTimeout(t);
  }, [isOpen, workspaceId, recent.length]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), 200);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    if (!isOpen || !workspaceId) return;
    const q = debouncedQuery;
    if (!q) {
      setResults([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    let cancelled = false;
    api
      .get(`contents/search/${workspaceId}?q=${encodeURIComponent(q)}&limit=20`)
      .then((res) => {
        if (cancelled) return;
        setResults(res.data.data || []);
      })
      .catch(() => {
        if (!cancelled) setResults([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedQuery, workspaceId, isOpen]);

  const showRecent = !debouncedQuery;
  const items = useMemo(
    () => (showRecent ? recent : results),
    [showRecent, recent, results],
  );

  const handleCopy = async (item: Content) => {
    setBusyId(item.id);
    const link = await ensurePublicLink(item);
    setBusyId(null);
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      toaster.create({ type: 'success', title: 'Public link copied to clipboard' });
    } catch {
      toaster.create({ type: 'error', title: 'Could not copy link to clipboard' });
    }
  };

  const handlePrint = (item: Content) => {
    onClose();
    void onPrint(item);
  };

  return (
    <DialogRoot lazyMount open={isOpen} onOpenChange={(e) => !e.open && onClose()}>
      <DialogBackdrop />
      <DialogContent maxW='720px'>
        <DialogCloseTrigger />
        <DialogHeader>
          <DialogTitle>Share content</DialogTitle>
          <Text fontSize='sm' color='fg.muted' mt={1}>
            Find what you want to share, then copy a link or print.
          </Text>
        </DialogHeader>
        <DialogBody pb={5}>
          <InputGroup
            startElement={<Icon as={MagnifyingGlassIcon} w={4} h={4} color='fg.subtle' />}
          >
            <Input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder='Search what you want to share...'
              bg='bg.subtle'
              borderColor='border'
              borderRadius='lg'
              size='lg'
              fontSize='md'
            />
          </InputGroup>

          <Box mt={4}>
            <Text
              fontSize='xs'
              fontWeight='bold'
              letterSpacing='wider'
              color='fg.muted'
              mb={2}
            >
              {showRecent ? 'RECENT — PICK SOMETHING TO SHARE' : 'RESULTS'}
            </Text>

            {isLoading && (
              <Flex justify='center' py={6}>
                <Spinner size='sm' color='fg.subtle' />
              </Flex>
            )}

            {!isLoading && items.length === 0 && (
              <Box py={6} textAlign='center'>
                <Text fontSize='sm' color='fg.muted'>
                  {showRecent
                    ? 'No content yet — start by creating one.'
                    : `No matches for "${debouncedQuery}".`}
                </Text>
              </Box>
            )}

            {!isLoading && items.length > 0 && (
              <Stack gap={1}>
                {items.map((item) => {
                  const ChoiceIcon = pickIcon(item.content_type_name);
                  const slot = slotFor(item.content_type_name || '');
                  const isBusy = busyId === item.id;
                  return (
                    <Flex
                      key={item.id}
                      align='center'
                      gap={3}
                      px={2}
                      py={2}
                      borderRadius='md'
                      width='100%'
                    >
                      <Flex
                        align='center'
                        justify='center'
                        w={9}
                        h={9}
                        borderRadius='md'
                        bg={`slot${slot}.bg`}
                        flexShrink={0}
                      >
                        <Icon as={ChoiceIcon} w={4} h={4} color={`slot${slot}.fg`} />
                      </Flex>
                      <Box flex='1' minW={0}>
                        <Text
                          fontSize='sm'
                          fontWeight='semibold'
                          color='fg.strong'
                          lineClamp={1}
                        >
                          {item.name || 'Untitled'}
                        </Text>
                        <Text fontSize='xs' color='fg.muted' lineClamp={1}>
                          {item.content_type_name || 'Content'}
                          {item.created_user && ` · ${item.created_user}`}
                        </Text>
                      </Box>
                      <Flex gap={2} flexShrink={0}>
                        <Button
                          size='sm'
                          variant='outline'
                          borderRadius='md'
                          loading={isBusy}
                          onClick={() => handleCopy(item)}
                        >
                          <Icon as={LinkIcon} w={4} h={4} />
                          Copy link
                        </Button>
                        <Button
                          size='sm'
                          variant='outline'
                          borderRadius='md'
                          loading={isBusy}
                          onClick={() => handlePrint(item)}
                        >
                          <Icon as={PrinterIcon} w={4} h={4} />
                          Print
                        </Button>
                      </Flex>
                    </Flex>
                  );
                })}
              </Stack>
            )}
          </Box>
        </DialogBody>
      </DialogContent>
    </DialogRoot>
  );
}
